import React, { useRef, useState } from "react";
import "./popup-menu.css";
import { Link } from "react-router-dom";
import { Popup, type PopupAlign, type PopupPlacement } from "@/components/ui-components/popup";
import { Icon } from "@/components/ui-components/icon";

export type PopupMenuItem = {
    id: string;
    label: React.ReactNode;
    icon?: React.ReactNode;
    disabled?: boolean;
    danger?: boolean;
    children?: PopupMenuItem[];
    to?: string;
    href?: string;
    onSelect?: () => void;
};

interface PopupMenuProps {
    open: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement | null>;
    items: PopupMenuItem[];
    placement?: PopupPlacement;
    align?: PopupAlign;
    offset?: number;
    offsetX?: number;
    offsetY?: number;
    xOffset?: number;
    yOffset?: number;
    className?: string;
    itemClassName?: string;
    submenuPlacement?: PopupPlacement;
    submenuAlign?: PopupAlign;
    submenuOffset?: number;
    submenuOffsetX?: number;
    submenuOffsetY?: number;
    submenuXOffset?: number;
    submenuYOffset?: number;
    preferPlacement?: boolean;
    submenuPreferPlacement?: boolean;
    id?: string;
    parentItemId?: string;
    width?: number | string;
    maxWidth?: number | string;
    minWidth?: number | string;
}

function PopupMenuItemRenderer({
    item,
    level,
    onClose,
    onSubmenuOpen,
    submenuOpenId,
    submenuRefs,
    itemClassName,
    submenuPlacement,
    submenuAlign,
    submenuOffset,
    submenuOffsetX,
    submenuOffsetY,
    submenuPreferPlacement,
}: {
    item: PopupMenuItem;
    level: number;
    onClose: () => void;
    onSubmenuOpen: (id: string | null) => void;
    submenuOpenId: string | null;
    submenuRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
    itemClassName?: string;
    submenuPlacement: PopupPlacement;
    submenuAlign: PopupAlign;
    submenuOffset: number;
    submenuOffsetX: number;
    submenuOffsetY: number;
    submenuPreferPlacement: boolean;
}) {
    const hasChildren = Boolean(item.children?.length);
    const isSubmenuOpen = submenuOpenId === item.id;

    const triggerSelection = () => {
        item.onSelect?.();
        if (!hasChildren) {
            onClose();
        }
    };

    const handleParentActivation = (event?: React.MouseEvent<HTMLElement>) => {
        if (!hasChildren) {
            item.onSelect?.();
            onClose();
            return;
        }

        if (event) {
            event.preventDefault();
        }

        onSubmenuOpen(isSubmenuOpen ? null : item.id);
    };

    const itemClasses = ["popup-menu__item", itemClassName, item.danger ? "popup-menu__item--danger" : "", hasChildren ? "popup-menu__item--has-children" : ""].filter(Boolean).join(" ");
    const closeSubmenu = () => {
        onSubmenuOpen(null);
        submenuRefs.current[item.id]?.focus();
    };

    if (item.to) {
        return (
            <React.Fragment key={item.id}>
                <Link
                    ref={element => {
                        if (element) submenuRefs.current[item.id] = element;
                    }}
                    to={item.to}
                    className={itemClasses}
                    data-menu-item-id={item.id}
                    data-menu-item-has-children={hasChildren ? "true" : "false"}
                    onClick={(event) => {
                        if (item.disabled) {
                            event.preventDefault();
                            return;
                        }
                        if (hasChildren) {
                            handleParentActivation(event);
                            return;
                        }
                        item.onSelect?.();
                        onClose();
                    }}
                    onMouseEnter={() => {
                        if (hasChildren && !item.disabled) onSubmenuOpen(item.id);
                    }}
                    role="menuitem"
                    aria-disabled={item.disabled || undefined}
                    aria-haspopup={hasChildren ? "menu" : undefined}
                    aria-expanded={hasChildren ? isSubmenuOpen : undefined}
                    tabIndex={item.disabled ? -1 : 0}
                >
                    {item.icon ? <span className="popup-menu__icon">{item.icon}</span> : null}
                    <span className="popup-menu__label">{item.label}</span>
                    {hasChildren ? <span className="popup-menu__chevron"><Icon name="chevron-right" size="sm" /></span> : null}
                </Link>
                {hasChildren && item.children ? (
                    <PopupMenu
                        open={isSubmenuOpen}
                        onClose={closeSubmenu}
                        anchorRef={{ current: submenuRefs.current[item.id] ?? null }}
                        items={item.children}
                        placement={submenuPlacement}
                        align={submenuAlign}
                        offset={submenuOffset}
                        offsetX={submenuOffsetX}
                        offsetY={submenuOffsetY}
                        className="popup-menu popup-menu--nested"
                        itemClassName={itemClassName}
                        submenuPlacement={submenuPlacement}
                        submenuAlign={submenuAlign}
                        submenuOffset={submenuOffset}
                        submenuOffsetX={submenuOffsetX}
                        submenuOffsetY={submenuOffsetY}
                        submenuPreferPlacement={submenuPreferPlacement}
                        parentItemId={item.id}
                    />
                ) : null}
            </React.Fragment>
        );
    }

    if (item.href) {
        return (
            <React.Fragment key={item.id}>
                <a
                    ref={element => {
                        if (element) submenuRefs.current[item.id] = element;
                    }}
                    href={item.href}
                    className={itemClasses}
                    data-menu-item-id={item.id}
                    data-menu-item-has-children={hasChildren ? "true" : "false"}
                    onClick={(event) => {
                        if (item.disabled) {
                            event.preventDefault();
                            return;
                        }
                        if (hasChildren) {
                            handleParentActivation(event);
                            return;
                        }
                        item.onSelect?.();
                        onClose();
                    }}
                    onMouseEnter={() => {
                        if (hasChildren && !item.disabled) onSubmenuOpen(item.id);
                    }}
                    role="menuitem"
                    aria-disabled={item.disabled || undefined}
                    aria-haspopup={hasChildren ? "menu" : undefined}
                    aria-expanded={hasChildren ? isSubmenuOpen : undefined}
                    tabIndex={item.disabled ? -1 : 0}
                >
                    {item.icon ? <span className="popup-menu__icon">{item.icon}</span> : null}
                    <span className="popup-menu__label">{item.label}</span>
                    {hasChildren ? <span className="popup-menu__chevron"><Icon name="chevron-right" size="sm" /></span> : null}
                </a>
                {hasChildren && item.children ? (
                    <PopupMenu
                        open={isSubmenuOpen}
                        onClose={closeSubmenu}
                        anchorRef={{ current: submenuRefs.current[item.id] ?? null }}
                        items={item.children}
                        placement={submenuPlacement}
                        align={submenuAlign}
                        offset={submenuOffset}
                        offsetX={submenuOffsetX}
                        offsetY={submenuOffsetY}
                        className="popup-menu popup-menu--nested"
                        itemClassName={itemClassName}
                        submenuPlacement={submenuPlacement}
                        submenuAlign={submenuAlign}
                        submenuOffset={submenuOffset}
                        submenuOffsetX={submenuOffsetX}
                        submenuOffsetY={submenuOffsetY}
                        submenuPreferPlacement={submenuPreferPlacement}
                        parentItemId={item.id}
                    />
                ) : null}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment key={item.id}>
            <button
                ref={element => {
                    if (element) {
                        submenuRefs.current[item.id] = element;
                    }
                }}
                type="button"
                className={itemClasses}
                data-menu-item-id={item.id}
                data-menu-item-has-children={hasChildren ? "true" : "false"}
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                    if (item.disabled) return;
                    if (hasChildren) {
                        onSubmenuOpen(isSubmenuOpen ? null : item.id);
                        return;
                    }
                    triggerSelection();
                }}
                onMouseEnter={() => {
                    if (hasChildren && !item.disabled) onSubmenuOpen(item.id);
                }}
                aria-haspopup={hasChildren ? "menu" : undefined}
                aria-expanded={hasChildren ? isSubmenuOpen : undefined}
            >
                {item.icon ? <span className="popup-menu__icon">{item.icon}</span> : null}
                <span className="popup-menu__label">{item.label}</span>
                {hasChildren ? <span className="popup-menu__chevron"><Icon name="chevron-right" size="sm" /></span> : null}
            </button>
            {hasChildren && item.children ? (
                <PopupMenu
                    open={isSubmenuOpen}
                    onClose={closeSubmenu}
                    anchorRef={{ current: submenuRefs.current[item.id] ?? null }}
                    items={item.children}
                    placement={submenuPlacement}
                    align={submenuAlign}
                    offset={submenuOffset}
                    className="popup-menu popup-menu--nested"
                    itemClassName={itemClassName}
                    submenuPlacement={submenuPlacement}
                    submenuAlign={submenuAlign}
                    submenuOffset={submenuOffset}
                    submenuPreferPlacement={submenuPreferPlacement}
                    offsetX={submenuOffsetX}
                    offsetY={submenuOffsetY}
                    parentItemId={item.id}
                />
            ) : null}
        </React.Fragment>
    );
}

export function PopupMenu({
    open,
    onClose,
    anchorRef,
    items,
    placement = "bottom",
    align = "start",
    offset = 8,
    offsetX,
    offsetY,
    xOffset,
    yOffset,
    className,
    itemClassName,
    submenuPlacement = "right",
    submenuAlign = "start",
    submenuOffset = 4,
    submenuOffsetX,
    submenuOffsetY,
    submenuXOffset,
    submenuYOffset,
    preferPlacement = false,
    submenuPreferPlacement = false,
    id,
    parentItemId,
    width,
    maxWidth,
    minWidth,
}: PopupMenuProps) {
    const [submenuOpenId, setSubmenuOpenId] = useState<string | null>(null);
    const submenuRefs = useRef<Record<string, HTMLElement | null>>({});
    const menuRef = useRef<HTMLDivElement | null>(null);
    const popupStyle = React.useMemo(
        () => parentItemId ? { width, minWidth, maxWidth, zIndex: 119 } : { width, minWidth, maxWidth },
        [maxWidth, minWidth, parentItemId, width]
    );
    const resolvedOffsetX = offsetX ?? xOffset ?? 0;
    const resolvedOffsetY = offsetY ?? yOffset ?? 0;
    const resolvedSubmenuOffsetX = submenuOffsetX ?? submenuXOffset ?? 0;
    const resolvedSubmenuOffsetY = submenuOffsetY ?? submenuYOffset ?? 0;

    React.useEffect(() => {
        if (!open) {
            setSubmenuOpenId(null);
            submenuRefs.current = {};
            return;
        }

        const focusable = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []);
        if (focusable.length > 0) {
            const active = document.activeElement as HTMLElement | null;
            if (!active || !menuRef.current?.contains(active)) {
                focusable[0]?.focus();
            }
        }
    }, [open]);

    React.useEffect(() => {
        if (!open) {
            setSubmenuOpenId(null);
            submenuRefs.current = {};
        }
    }, [open]);

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!menuRef.current) return;
        const itemsInMenu = Array.from(menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])'));
        if (itemsInMenu.length === 0) return;

        const currentIndex = itemsInMenu.indexOf(document.activeElement as HTMLElement);

        if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % itemsInMenu.length;
            itemsInMenu[nextIndex]?.focus();
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            const nextIndex = currentIndex < 0 ? itemsInMenu.length - 1 : (currentIndex - 1 + itemsInMenu.length) % itemsInMenu.length;
            itemsInMenu[nextIndex]?.focus();
            return;
        }

        if (event.key === "ArrowRight") {
            const currentItem = currentIndex >= 0 ? itemsInMenu[currentIndex] : null;
            const itemId = currentItem?.getAttribute("data-menu-item-id");
            const hasChildren = currentItem?.getAttribute("data-menu-item-has-children") === "true";
            if (itemId && hasChildren) {
                event.preventDefault();
                if (submenuOpenId !== itemId) {
                    setSubmenuOpenId(itemId);
                } else {
                    const firstSubmenuItem = document.querySelector<HTMLElement>(`[data-popup-parent-id="${itemId}"] [role="menuitem"]:not([aria-disabled="true"])`);
                    firstSubmenuItem?.focus();
                }
            }
            return;
        }

        if (event.key === "ArrowLeft") {
            if (submenuOpenId) {
                event.preventDefault();
                setSubmenuOpenId(null);
                const parentTrigger = menuRef.current?.querySelector<HTMLElement>(`[data-menu-item-id="${submenuOpenId}"]`);
                parentTrigger?.focus();
            } else if (parentItemId) {
                event.preventDefault();
                onClose();
            }
            return;
        }

        if (event.key === "Home") {
            event.preventDefault();
            itemsInMenu[0]?.focus();
            return;
        }

        if (event.key === "End") {
            event.preventDefault();
            itemsInMenu[itemsInMenu.length - 1]?.focus();
            return;
        }

        if ((event.key === "Enter" || event.key === " ") && currentIndex >= 0) {
            const currentItem = itemsInMenu[currentIndex];
            if (currentItem instanceof HTMLButtonElement || currentItem instanceof HTMLAnchorElement) {
                event.preventDefault();
                currentItem.click();
            }
        }
    };

    return (
        <Popup
            open={open}
            onClose={onClose}
            anchorRef={anchorRef}
            placement={placement}
            align={align}
            offset={offset}
            offsetX={resolvedOffsetX}
            offsetY={resolvedOffsetY}
            preferPlacement={parentItemId ? submenuPreferPlacement : preferPlacement}
            className={className ? `${className} popup-menu` : "popup-menu"}
            id={id}
            style={popupStyle}
            closeOnEscape
            closeOnOutsidePointerDown
            autoFocus
            restoreFocus
            role="menu"
            aria-label="Popup menu"
            data-popup-parent-id={parentItemId}
            onKeyDown={handleMenuKeyDown}
        >
            <div ref={menuRef} className="popup-menu__list" role="menu">
                {items.map(item => (
                    <PopupMenuItemRenderer
                        key={item.id}
                        item={item}
                        level={0}
                        onClose={onClose}
                        onSubmenuOpen={setSubmenuOpenId}
                        submenuOpenId={submenuOpenId}
                        submenuRefs={submenuRefs}
                        itemClassName={itemClassName}
                        submenuPlacement={submenuPlacement}
                        submenuAlign={submenuAlign}
                        submenuOffset={submenuOffset}
                        submenuOffsetX={resolvedSubmenuOffsetX}
                        submenuOffsetY={resolvedSubmenuOffsetY}
                        submenuPreferPlacement={submenuPreferPlacement}
                    />
                ))}
            </div>
        </Popup>
    );
}

export default PopupMenu;
